import { useCallback, useMemo } from 'react';
import { from, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { match, P } from 'ts-pattern';
import { DraftData, syncDraft } from '../../api/Drafts';
import { useAuthenticateApiCall } from '../hooks/useAuthenticateApiCall';
import { useSubscription } from '../hooks/useSubscription';
import { BusDispatch, BusEvent } from './EditorContext';

const usePersistDraft = (
  attemptSync$: Observable<void>,
  interrupt$: Observable<void>,
  document: DraftData | undefined,
  dispatch: BusDispatch
) => {
  const authedSyncDraft = useAuthenticateApiCall(syncDraft);

  const makeSyncStream = useCallback(
    () =>
      from(
        authedSyncDraft(document?.id as string, document?.title as string, document?.body as string)
      ).pipe(takeUntil(interrupt$)),
    [authedSyncDraft, document, interrupt$]
  );

  const sync$ = useMemo(
    () => attemptSync$.pipe(switchMap(makeSyncStream), takeUntil(interrupt$)),
    [attemptSync$, interrupt$, makeSyncStream]
  );

  return useSubscription(
    () =>
      sync$.subscribe({
        next: (res) => {
          match(res)
            .with({ status: 'ok' }, () =>
              dispatch({ type: 'Editor', event: { type: 'SYNC_SUCCESSFUL' } })
            )
            .with({ status: 'failed' }, () =>
              dispatch({ type: 'Editor', event: { type: 'SYNC_FAILED' } })
            )
            .with({ status: 'error', errors: P.select() }, (errors) => {
              match(errors[0].message)
                .with('Size Limit', () => {
                  dispatch({ type: 'Notify', event: 'LengthError' });
                })
                .exhaustive();
            })
            .exhaustive();
        },
        error: (_res) => {
          dispatch({ type: 'Editor', event: { type: 'SYNC_FAILED' } });
        },
      }),
    [sync$, dispatch]
  );
};

export const useDraftPersistence = (
  bus$: Subject<BusEvent>,
  dispatch: BusDispatch,
  document: DraftData | undefined
) => {
  const attemptSync$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Editor' && e.event.type === 'INPUT_END'),
        map(() => {})
      ),
    [bus$]
  );

  const interrupt$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Editor' && e.event.type === 'INPUT'),
        map(() => {})
      ),
    [bus$]
  );

  usePersistDraft(attemptSync$, interrupt$, document, dispatch);
};
